import { Link } from 'react-router-dom'
import { DataTable, type DataTableColumn } from '@/components/DataTable'
import { RowActions } from '@/components/RowActions'
import type { ProductType } from '@/types/bundle'

export interface ProductTypeRow {
  id: string
  product: ProductType
  lastEdited?: string
}

export interface ProductTypesTableProps {
  rows: ProductTypeRow[]
  onDuplicate: (row: ProductTypeRow) => void
  onDelete: (row: ProductTypeRow) => void
}

function requiredDocumentTypeCount(product: ProductType): number {
  const rules = product.document_requirements.rules ?? {}
  const ids = new Set<string>(product.document_requirements.hard_floor ?? [])
  ;(rules.must_have_all ?? []).forEach((id) => ids.add(id))
  ;(rules.must_have_any_of ?? []).forEach((group) => group.forEach((id) => ids.add(id)))
  return ids.size
}

function formatDate(value?: string): string {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function ProductTypesTable({ rows, onDuplicate, onDelete }: ProductTypesTableProps) {
  const columns: DataTableColumn<ProductTypeRow>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <Link to={`/product-types/${row.id}`} className="text-accent hover:underline">
          {row.product.description ?? row.id}
        </Link>
      ),
    },
    {
      key: 'mode',
      header: 'Mode',
      render: (row) => (
        <span className="inline-flex items-center rounded-pill bg-surface-raised px-2 py-1 text-meta text-text-primary">
          {row.product.mode}
        </span>
      ),
    },
    {
      key: 'documentTypesRequired',
      header: 'Document types required',
      align: 'right',
      render: (row) => requiredDocumentTypeCount(row.product),
    },
    {
      key: 'lastEdited',
      header: 'Last edited',
      render: (row) => formatDate(row.lastEdited),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => <RowActions onDuplicate={() => onDuplicate(row)} onDelete={() => onDelete(row)} />,
    },
  ]

  return <DataTable columns={columns} data={rows} getRowKey={(row) => row.id} />
}
